import { useCallback, useEffect, useRef, useState } from "react"

export { formatElapsedTimeText } from "./translations"

export function formatElapsedTimeNumbers(time) {
  const totalHundredths = Math.max(0, Math.round(time * 100))
  const minutes = Math.floor(totalHundredths / 6000)
  const seconds = Math.floor((totalHundredths % 6000) / 100)
  const hundredths = totalHundredths % 100

  return `${minutes}:${String(seconds).padStart(2, "0")}:${String(hundredths).padStart(2, "0")}`
}

function useGameClock() {
  const [elapsedTime, setElapsedTime] = useState(0)
  const elapsedMsRef = useRef(0)
  const startMarkRef = useRef(0)
  const modeRef = useRef("stopped")
  const frameRef = useRef(null)

  const cancelFrame = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [])

  const getElapsedTime = useCallback(() => {
    if (modeRef.current === "running") {
      return (
        (elapsedMsRef.current + (performance.now() - startMarkRef.current)) /
        1000
      )
    }

    return elapsedMsRef.current / 1000
  }, [])

  const scheduleFrame = useCallback(() => {
    cancelFrame()

    frameRef.current = requestAnimationFrame(function tick(now) {
      if (modeRef.current !== "running") {
        return
      }

      setElapsedTime(
        (elapsedMsRef.current + (now - startMarkRef.current)) / 1000
      )
      frameRef.current = requestAnimationFrame(tick)
    })
  }, [cancelFrame])

  const startClock = useCallback(() => {
    elapsedMsRef.current = 0
    startMarkRef.current = performance.now()
    setElapsedTime(0)

    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      modeRef.current = "paused"
      cancelFrame()
      return
    }

    modeRef.current = "running"
    scheduleFrame()
  }, [cancelFrame, scheduleFrame])

  const resetClock = useCallback(() => {
    elapsedMsRef.current = 0
    startMarkRef.current = performance.now()
    setElapsedTime(0)

    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      modeRef.current = "paused"
      cancelFrame()
      return
    }

    modeRef.current = "running"
    scheduleFrame()
  }, [cancelFrame, scheduleFrame])

  const pauseClock = useCallback(() => {
    if (modeRef.current !== "running") {
      return
    }

    elapsedMsRef.current += performance.now() - startMarkRef.current
    modeRef.current = "paused"
    cancelFrame()
    setElapsedTime(elapsedMsRef.current / 1000)
  }, [cancelFrame])

  const stopClock = useCallback(() => {
    if (modeRef.current === "running") {
      elapsedMsRef.current += performance.now() - startMarkRef.current
    }

    modeRef.current = "stopped"
    cancelFrame()
    setElapsedTime(elapsedMsRef.current / 1000)
  }, [cancelFrame])

  useEffect(() => {
    function handleVisibilityChange() {
      if (typeof document === "undefined") {
        return
      }

      if (document.visibilityState === "hidden") {
        pauseClock()
        return
      }

      if (modeRef.current === "paused") {
        startMarkRef.current = performance.now()
        modeRef.current = "running"
        scheduleFrame()
        return
      }

      if (modeRef.current === "running" && frameRef.current === null) {
        scheduleFrame()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      modeRef.current = "stopped"
      cancelFrame()
    }
  }, [cancelFrame, pauseClock, scheduleFrame])

  useEffect(() => {
    startClock()

    return () => {
      modeRef.current = "stopped"
      cancelFrame()
    }
  }, [cancelFrame, startClock])

  return {
    elapsedTime,
    getElapsedTime,
    resetClock,
    stopClock,
  }
}

export default useGameClock
